import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import type { User } from '@skyrise-eng/types';
import { NAMESPACE } from '@/constants';
import { COLUMNS, Column, RoleDetails } from '../UserSettings.constants';
import type { UseUserSettings, UserList, UserListColumns } from '../UserSettings.types';
import { Tag } from '@/components';
import { Actions } from '../components';
import { usePagination } from '@/hooks/usePagination';
import { useGetUserListQuery } from '@/api';
import { useModal } from '@/hooks/useModal';

const { RoleTag } = Tag;

export const useUserSettings: UseUserSettings = () => {
  const { isOpen: isUserFormOpen, open: openUserForm, close: closeUserForm } = useModal();
  const { page, limit, handlePaginationChange } = usePagination({ page: 1, limit: 10 });
  const { data, isFetching } = useGetUserListQuery({ page, limit });
  const [user, setUser] = useState<User>();
  const { t } = useTranslation(NAMESPACE.USER_SETTINGS_PAGE);

  const handleOpenUserForm = useCallback(
    (values?: User) => {
      setUser(values);
      openUserForm();
    },
    [openUserForm]
  );

  const columns = useMemo((): UserListColumns => {
    COLUMNS[Column.NAME].render = (_, { firstName, lastName }) => [firstName, lastName].join(' ');
    COLUMNS[Column.ROLE].render = (_, { role }) => {
      const { name, className } = RoleDetails[role];

      return <RoleTag className={className}>{name}</RoleTag>;
    };
    COLUMNS[Column.ACTIONS].render = (_, { _id, ...rest }) => (
      <Actions id={_id} edit={() => handleOpenUserForm({ _id, ...rest })} />
    );

    return Object.values(COLUMNS).map(({ i18nKey, ...rest }) => ({
      title: t(i18nKey),
      ...rest
    }));
  }, [handleOpenUserForm, t]);

  const userList: UserList = useMemo(
    () => data?.data.map((userData) => ({ key: userData._id, ...userData })) || [],
    [data]
  );

  return {
    closeUserForm,
    columns,
    handleOpenUserForm,
    handlePaginationChange,
    isFetching,
    isUserFormOpen,
    openUserForm,
    user,
    userList,
    limit,
    count: data?.count,
    page
  };
};
